/**
 * Papapattes — Scroll infini pour les pages collection.
 *
 * Fonctionne pour toute collection (smart, manuelle/"sélection") car elles
 * partagent le même template Liquid `templates/collection.json` et la même
 * section `main-collection-product-grid`.
 *
 * Compatible avec le filtrage/tri AJAX natif de Dawn (facets.js) :
 * - facets.js remplace #ProductGridContainer quand un filtre/tri change ;
 *   un MutationObserver détecte ce remplacement et réobserve le nouveau
 *   lien "page suivante".
 * - Le scroll infini n'ajoute que des <li> à la fin de #product-grid et ne
 *   touche jamais aux filtres eux-mêmes.
 *
 * Amélioration progressive : sans JS, la pagination classique (?page=N)
 * fonctionne nativement — rien de tout ceci n'est requis pour l'indexation SEO.
 *
 * La navigation entre fiches produit (Suivant/Précédent) est gérée
 * directement sur la page produit elle-même, en navigation classique
 * (voir sections/main-product.liquid) — ce fichier ne s'occupe que du
 * chargement progressif de la grille.
 *
 * PAPAPATTES — Support de la page recherche (templates/search.json) :
 * la structure Dawn diffère entre collection et recherche. Sur une
 * collection, #product-grid EST le <ul> contenant les <li> produits.
 * Sur la recherche, #product-grid est le <div> englobant (résultats +
 * pagination) et le <ul> à l'intérieur n'a pas d'id. `resolveGrid` gère
 * les deux cas pour que le scroll infini fonctionne identiquement partout.
 */

class CollectionInfiniteScroll {
  constructor() {
    this.isLoadingPage = false;
    this.observer = null;
    this.observeNextPageLink();
    this.watchGridReplacement();
  }

  // PAPAPATTES — résout l'élément qui contient réellement les <li> produits.
  // Sur les pages collection, #product-grid EST le <ul> lui-même.
  // Sur la page recherche (structure Dawn différente), #product-grid est un
  // <div> englobant (résultats + pagination) qui contient un <ul> sans id ;
  // il faut alors descendre d'un cran pour trouver la vraie liste.
  resolveGrid(container) {
    if (!container) return null;
    return container.tagName === 'UL' ? container : container.querySelector('ul');
  }

  observeNextPageLink() {
    if (this.observer) this.observer.disconnect();

    const nextLink = document.querySelector('[data-infinite-scroll-next]');
    if (!nextLink) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.isLoadingPage) {
          this.loadNextPage(entries[0].target);
        }
      },
      { rootMargin: '150px', threshold: 0.01 }
    );
    this.observer.observe(nextLink);
  }

  async loadNextPage(nextLinkEl) {
    this.isLoadingPage = true;
    if (this.observer) this.observer.unobserve(nextLinkEl);

    const container = document.getElementById('product-grid');
    const grid = this.resolveGrid(container);
    const sectionId = container ? container.dataset.id : null;
    if (!container || !grid || !sectionId) {
      this.isLoadingPage = false;
      return;
    }

    const targetUrl = nextLinkEl.href;
    const separator = targetUrl.includes('?') ? '&' : '?';
    const fetchUrl = `${targetUrl}${separator}section_id=${sectionId}`;

    try {
      const response = await fetch(fetchUrl);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newContainer = doc.getElementById('product-grid');
      const newGrid = this.resolveGrid(newContainer);
      const newPaginationWrapper = doc.querySelector('.pagination-wrapper');

      if (newGrid) {
        Array.from(newGrid.children).forEach((li) => grid.appendChild(li));
      }

      // Met à jour l'URL canonique affichée sans recharger ni empiler l'historique
      history.replaceState(history.state, '', targetUrl);

      const currentPaginationWrapper = document.querySelector('.pagination-wrapper');
      const newNextLink = newPaginationWrapper
        ? newPaginationWrapper.querySelector('[data-infinite-scroll-next]')
        : null;

      if (currentPaginationWrapper) {
        if (newPaginationWrapper) {
          currentPaginationWrapper.replaceWith(newPaginationWrapper);
        } else {
          currentPaginationWrapper.remove(); // dernière page atteinte
        }
      }

      if (newNextLink && this.observer) {
        // Petite pause avant de réobserver : évite qu'un nouveau lien
        // encore visible ne redéclenche instantanément un autre fetch
        // en cascade juste après celui qui vient de se terminer.
        const observerRef = this.observer;
        setTimeout(() => observerRef.observe(newNextLink), 400);
      }
    } catch (err) {
      console.error('Scroll infini — échec du chargement de la page suivante', err);
    } finally {
      this.isLoadingPage = false;
    }
  }

  // Quand facets.js remplace #ProductGridContainer (filtre/tri appliqué),
  // on réobserve le nouveau lien "page suivante"
  watchGridReplacement() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    const mutationObserver = new MutationObserver(() => {
      this.observeNextPageLink();
    });
    mutationObserver.observe(container, { childList: true, subtree: false });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (document.getElementById('product-grid')) {
    new CollectionInfiniteScroll();
  }
});
